import React, { useState, useEffect, useCallback } from "react";
import {
  ShieldAlert,
  User,
  Clock,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Building2,
  Activity,
  RefreshCw,
  AlertTriangle,
  Info,
  X
} from "lucide-react";
import PageHeader from "../../components/PageHeader";
import LoadingScreen from "../../components/LoadingScreen";
import { getAuditLogs } from "../../services/adminAudit";

const actionLabels = {
  admin_company_created: "Empresa criada",
  admin_company_updated: "Empresa atualizada",
  admin_company_activated: "Empresa ativada",
  admin_company_deactivated: "Empresa desativada",
  admin_user_created: "Usuário criado",
  admin_user_updated: "Usuário atualizado",
  admin_user_super_granted: "Superadmin concedido",
  admin_user_super_revoked: "Superadmin revogado",
  admin_announcement_created: "Informativo criado",
  admin_announcement_updated: "Informativo atualizado",
  admin_announcement_deleted: "Informativo excluído",
  admin_connection_diagnosed: "Conexão diagnosticada",
  admin_connection_repaired: "Conexão reparada",
  admin_connection_restarted: "Conexão reiniciada",
  admin_global_setting_updated: "Configuração global atualizada",
  admin_api_preset_created: "API Preset criado",
  admin_api_preset_updated: "API Preset atualizado",
  admin_api_preset_deleted: "API Preset excluído",
  admin_plan_updated: "Plano atualizado"
};

const entityTypeLabels = {
  company: "Empresa",
  user: "Usuário",
  announcement: "Informativo",
  connection: "Conexão",
  setting: "Configuração",
  api_preset: "API Preset",
  plan: "Plano"
};

const formatTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit"
    });
  } catch { return "—"; }
};

const Audit = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ search: "", entityType: "", action: "" });
  const [showFilters, setShowFilters] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const loadLogs = useCallback(async (p = page, f = filters) => {
    setLoading(true);
    setError("");
    try {
      const params = { pageNumber: p, pageSize: 25, sortOrder: "DESC" };
      if (f.search) params.search = f.search;
      if (f.entityType) params.entityType = f.entityType;
      if (f.action) params.action = f.action;
      const data = await getAuditLogs(params);
      setLogs(data.items || []);
      setPagination(data.pagination || null);
    } catch (err) {
      setError(err.message || "Erro ao carregar auditoria.");
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { loadLogs(); }, []);

  const handleSearch = () => { setPage(1); loadLogs(1, filters); };
  const handlePage = (p) => { setPage(p); loadLogs(p, filters); };
  const clearFilters = () => { setFilters({ search: "", entityType: "", action: "" }); setPage(1); loadLogs(1, { search: "", entityType: "", action: "" }); };

  if (loading && logs.length === 0) return <LoadingScreen message="Carregando trilha de auditoria..." />;

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="Controle & Segurança"
        title="Trilha de Auditoria"
        subtitle="Registro de ações administrativas globais"
        action={
          <button onClick={() => loadLogs()} className="flex items-center gap-2 px-4 py-2 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 border border-gray-200 dark:border-white/10 text-xs font-semibold text-gray-700 dark:text-gray-200 rounded-xl transition-all">
            <RefreshCw size={14} /> Atualizar
          </button>
        }
      />

      {error && (
        <div className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-300 text-xs font-semibold flex items-center gap-3">
          <AlertTriangle size={18} /> {error}
        </div>
      )}

      {/* Search + Filters */}
      <div className="neumorphic-card rounded-2xl p-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="flex-1 flex items-center bg-black/5 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl px-3 py-2">
            <Search size={14} className="text-gray-400 mr-2" />
            <input
              type="text"
              placeholder="Buscar por email, ação, entidade..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="bg-transparent border-none outline-none text-xs text-gray-800 dark:text-gray-200 placeholder-gray-400 w-full"
            />
          </div>
          <button onClick={handleSearch} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-xl transition-all">
            Buscar
          </button>
          <button onClick={() => setShowFilters(!showFilters)} className="p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-black/5 dark:hover:bg-white/5 border border-gray-200 dark:border-white/10">
            <Filter size={14} />
          </button>
          {(filters.entityType || filters.action) && (
            <button onClick={clearFilters} className="p-2 rounded-xl text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10">
              <X size={14} />
            </button>
          )}
        </div>

        {showFilters && (
          <div className="flex gap-3 pt-2 border-t border-gray-100 dark:border-white/5">
            <select
              value={filters.entityType}
              onChange={(e) => setFilters({ ...filters, entityType: e.target.value })}
              className="px-3 py-1.5 bg-black/5 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-700 dark:text-gray-300"
            >
              <option value="">Todas entidades</option>
              <option value="company">Empresa</option>
              <option value="user">Usuário</option>
              <option value="announcement">Informativo</option>
              <option value="connection">Conexão</option>
              <option value="setting">Configuração</option>
              <option value="api_preset">API Preset</option>
            </select>
            <select
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value })}
              className="px-3 py-1.5 bg-black/5 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-700 dark:text-gray-300"
            >
              <option value="">Todas ações</option>
              {Object.entries(actionLabels).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Logs List */}
      {logs.length === 0 && !loading ? (
        <div className="neumorphic-card rounded-2xl p-8 text-center">
          <ShieldAlert size={32} className="mx-auto text-gray-400 dark:text-gray-500 mb-3" />
          <p className="text-xs text-gray-500 dark:text-gray-400">Nenhum registro de auditoria encontrado.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {logs.map((log) => (
            <div key={log.id} className="neumorphic-card rounded-2xl p-4 border transition-all hover:border-purple-500/20">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-600 dark:text-purple-400 flex items-center justify-center flex-shrink-0">
                    <Activity size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold text-gray-900 dark:text-white">
                        {actionLabels[log.action] || log.action}
                      </span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400">
                        {entityTypeLabels[log.entityType] || log.entityType}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-[10px] text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <User size={10} /> {log.actorName || log.actorEmail}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={10} /> {formatTime(log.createdAt)}
                      </span>
                      {log.companyId && (
                        <span className="flex items-center gap-1">
                          <Building2 size={10} /> #{log.companyId}
                        </span>
                      )}
                      {log.ipAddress && (
                        <span className="font-mono">{log.ipAddress}</span>
                      )}
                    </div>
                  </div>
                </div>
                <ChevronRight
                  size={16}
                  className={`text-gray-400 transition-transform flex-shrink-0 ${expandedId === log.id ? "rotate-90" : ""}`}
                />
              </div>

              {expandedId === log.id && (
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-white/5 space-y-2 text-xs">
                  {log.reason && (
                    <div><span className="text-gray-500 dark:text-gray-400">Motivo:</span> <span className="text-gray-700 dark:text-gray-200">{log.reason}</span></div>
                  )}
                  {log.oldValue && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400 font-semibold">Valores anteriores:</span>
                      <pre className="mt-1 p-2 rounded-lg bg-gray-50 dark:bg-white/5 text-[10px] text-gray-600 dark:text-gray-300 overflow-x-auto">
                        {JSON.stringify(log.oldValue, null, 2)}
                      </pre>
                    </div>
                  )}
                  {log.newValue && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400 font-semibold">Novos valores:</span>
                      <pre className="mt-1 p-2 rounded-lg bg-gray-50 dark:bg-white/5 text-[10px] text-gray-600 dark:text-gray-300 overflow-x-auto">
                        {JSON.stringify(log.newValue, null, 2)}
                      </pre>
                    </div>
                  )}
                  {log.metadata && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400 font-semibold">Metadados:</span>
                      <pre className="mt-1 p-2 rounded-lg bg-gray-50 dark:bg-white/5 text-[10px] text-gray-600 dark:text-gray-300 overflow-x-auto">
                        {JSON.stringify(log.metadata, null, 2)}
                      </pre>
                    </div>
                  )}
                  {log.userAgent && (
                    <div className="text-[10px] text-gray-400 dark:text-gray-500 font-mono truncate">
                      UA: {log.userAgent}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-gray-500 dark:text-gray-400">
            {pagination.total} registro(s) · Página {pagination.pageNumber}/{pagination.totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePage(pagination.pageNumber - 1)}
              disabled={pagination.pageNumber <= 1}
              className="p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-30"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-xs font-bold text-gray-700 dark:text-gray-200">
              {pagination.pageNumber}
            </span>
            <button
              onClick={() => handlePage(pagination.pageNumber + 1)}
              disabled={!pagination.hasMore}
              className="p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-30"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Audit;
